import React, { useMemo, useState } from 'react'

import { Group, TextInput } from '@tidbcloud/uikit'
import { TimeRangePicker } from '@tidbcloud/uikit/biz'
import { IconXClose } from '@tidbcloud/uikit/icons'
import dayjs from 'dayjs'
import { useQuery } from 'react-query'

import { eventTracking } from '~/utils/telemetry'

import { useStatementContext } from '../utils/context'
import { FEATURE_NAME, QUICK_RANGES } from '../utils/helpers'
import { FilterMultiSelect } from './FilterMultiSelect'
import { useStatementListUrlState } from './url-state'

interface StatementFiltersProps {
  isLoading: boolean
  enableFetchData: boolean
}

export const StatementFilters: React.FC<
  React.PropsWithChildren<StatementFiltersProps>
> = ({ isLoading, enableFetchData }) => {
  const ctx = useStatementContext()

  const { timeRange, setTimeRange, kinds, setKinds, search, setSearch } =
    useStatementListUrlState()

  const { data: stmtKinds } = useQuery(
    ['statement', 'stmt_types', ctx.cfg.clusterId],
    () => ctx.ds.getStmtKinds(),
    {
      refetchOnWindowFocus: false,
      enabled: enableFetchData
    }
  )

  const [keyword, setKeyword] = useState(search)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSearch(keyword)
    eventTracking(`${FEATURE_NAME} Keyword Filter Changed`)
  }

  const timeRangePicker = (
    <TimeRangePicker
      value={timeRange}
      onChange={(v) => {
        setTimeRange(v)
        eventTracking(`${FEATURE_NAME} Time Range Filter Clicked`, {
          timeRange: v
        })
      }}
      quickRanges={QUICK_RANGES}
      minDateTime={() =>
        dayjs()
          .subtract(QUICK_RANGES[QUICK_RANGES.length - 1], 'seconds')
          .toDate()
      }
      maxDateTime={() => dayjs().toDate()}
      loading={isLoading}
    />
  )

  const selectData = useMemo(
    () => (stmtKinds || []).map((k) => ({ label: k, value: k })),
    [stmtKinds]
  )
  const kindSelect = (
    <FilterMultiSelect
      kind='Statement Kinds'
      width={240}
      value={kinds}
      data={selectData}
      onChange={(v) => {
        eventTracking(`${FEATURE_NAME} Kind Filter Clicked`)
        setKinds(v)
      }}
      disabled={isLoading}
    />
  )

  const keywordInput = (
    <TextInput
      w={200}
      value={keyword}
      onChange={(e: any) => setKeyword(e.target.value)}
      placeholder='Filter Keyword'
      rightSection={
        !!keyword && (
          <IconXClose
            style={{ cursor: 'pointer' }}
            size={14}
            onClick={() => {
              setKeyword('')
              setSearch('')
            }}
          />
        )
      }
      disabled={isLoading}
    />
  )

  return (
    <form onSubmit={handleSubmit}>
      <Group>
        {timeRangePicker}
        {kindSelect}
        {keywordInput}
        <button type='submit' style={{ display: 'none' }} />
      </Group>
    </form>
  )
}
